import { FC, useContext, useMemo } from 'react';
import { IressButton, IressInline, IressPanel, IressSkeleton, IressText } from '@iress-oss/ids-components';
import { useNavigate } from 'react-router';
import { FormHandlingContext } from '../../context/FormHandlingContext';
import { usePageSearchParams } from '../../hooks';
import { useEntityDetailsQuery } from '../../services';
import { LoadingAndErrorHandler } from '../LoadingAndErrorHandler/LoadingAndErrorHandler';
import { isNullOrUndefinedOrEmpty } from '../../helpers/IsNullOrUndefinedOrEmpty/IsNullOrUndefinedOrEmpty';

export type HeaderNavProperties = {
    title: string;
    withSubheading?: boolean;
    withButtons?: boolean;
};
const HeadingWithSubheading = ({ title, withSubheading }: HeaderNavProperties) => {
    const { scenarioId, entityId, ownerId } = usePageSearchParams();
    const {
        data: { client, partner },
    } = useEntityDetailsQuery(entityId);

    const subHeading = useMemo(() => {
        if (withSubheading && ownerId && scenarioId) {
            const forName = ownerId === client.id ? client.entityName : partner?.entityName;
            return !isNullOrUndefinedOrEmpty(forName) ? ` for: ${forName}` : '';
        }
        return '';
    }, [client.entityName, client.id, ownerId, partner?.entityName, scenarioId, withSubheading]);
    return (
        <IressText data-testid="screen-heading" noGutter element="h1">
            {`${title}${subHeading}`}
        </IressText>
    );
};

export const PageHeaderWithNavigation: FC<HeaderNavProperties> = ({ title, withSubheading, withButtons }) => {
    const navigate = useNavigate();
    const { entityId } = usePageSearchParams();
    const { search, previousPage, nextPage, onFormSubmit } = useContext(FormHandlingContext);

    return (
        <IressPanel background="page" padding={{ y: 'md', x: 'none' }} noBorderRadius data-testid="page-action-buttons">
            <IressInline horizontalAlign="between" verticalAlign="top" gutter="lg" noWrap>
                <LoadingAndErrorHandler
                    LoadingComponentFallback={<IressSkeleton mode="text" textVariant="display4" width="50%" />}
                    ErrorComponentFallback={
                        <IressText variant="display4" data-testid="entity-details__error">
                            Unable to load entity details
                        </IressText>
                    }
                >
                    <HeadingWithSubheading withSubheading={withSubheading} title={title} />
                </LoadingAndErrorHandler>
                {withButtons && (
                    <IressInline gutter="md" noWrap>
                        <IressButton
                            id={'btnBack'}
                            mode={'secondary'}
                            data-testid={'btnBack'}
                            onClick={() => {
                                let navigateTo;
                                if (!previousPage) {
                                    if (entityId) {
                                        navigateTo = {
                                            pathname: '/',
                                            search: `?entityid=${entityId}`,
                                        };
                                    } else {
                                        navigateTo = { pathname: '/' };
                                    }
                                } else {
                                    navigateTo = {
                                        pathname: `/${previousPage}`,
                                        search: `${search}`,
                                    };
                                }
                                navigate(navigateTo);
                            }}
                        >
                            Back
                        </IressButton>
                        <IressButton
                            mode={'primary'}
                            type={'submit'}
                            data-testid={'btnSaveAndContinue'}
                            onClick={onFormSubmit}
                        >
                            {nextPage ? 'Save & continue' : 'Save & exit'}
                        </IressButton>
                    </IressInline>
                )}
            </IressInline>
        </IressPanel>
    );
};
